/**
 * FarmPacks — skeleton placeholders.
 *
 * Two jobs, both for things that genuinely make you wait:
 *
 *  1. Media. Shopify serves the document complete, so the only part of a
 *     first paint still pending is the photography. Every media box gets a
 *     shimmer until its image decodes, then the image fades in over it.
 *
 *  2. The collection reload. The filter bar navigates rather than fetching,
 *     so a busy collection can sit on the old grid for a beat with nothing
 *     to show for the click. Swapping the grid for skeleton cards on the way
 *     out makes the control feel answered.
 *
 * Copy is never skeletonised: it ships with the HTML, so a placeholder there
 * would flash rather than fill a gap.
 */
(function () {
  if (window.__fpSkeletonBound) return;
  window.__fpSkeletonBound = true;

  /**
   * Boxes are found from the image up, not the other way round: a collection
   * card nests its photo inside a slideshow several levels down, while a V2
   * card holds it directly, and `closest` handles both without this file
   * having to know either shape.
   *
   * The shimmer paints as the box's own background, so — unlike the overlay
   * this replaced — it needs no positioning and no measurement pass.
   */
  var BOX = [
    '.product-media',
    '.fp-product-card__media',
    '.fp-tile__media',
    '.fp-finder-cat__media',
    '.fp-row__media',
    '.fp-cta__media',
    '.fp-hero__media',
    '.fp-flagship__media',
    '.blog-post-card__image-container',
    '.blog-post-featured-image',
  ].join(',');

  // Logos, chips and icons are furniture rather than content: they are small,
  // land almost immediately, and shimmer boxes around them would just flicker.
  var EXCLUDE = ['.fp-hero__media', '.fp-brand-chip', '.fp-brand-mark', 'marquee-component', '.header', '.fp-search-suggest'].join(',');

  // Cards dim their badges while the photo behind them is still missing.
  var CARD = '.card-gallery, .fp-product-card__media';

  var seen = new WeakSet();

  function ready(box, card) {
    box.setAttribute('data-fp-media', 'ready');
    if (card) card.setAttribute('data-fp-card', 'ready');
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var images = [].slice.call(scope.querySelectorAll('img'));
    if (scope !== document && scope.tagName === 'IMG') images.push(scope);

    for (var i = 0; i < images.length; i++) {
      var img = images[i];
      if (seen.has(img)) continue;
      seen.add(img);
      if (img.closest(EXCLUDE)) continue;

      var box = img.closest(BOX);
      if (!box) continue;

      var card = img.closest(CARD);

      // The eager, high-priority image is the one the page is judged on, and
      // it is already at the front of the queue. A shimmer behind it buys
      // nothing and only risks getting in the way of the paint that counts.
      if (img.loading === 'eager' || img.getAttribute('fetchpriority') === 'high') {
        ready(box, card);
        continue;
      }

      // Anything already in cache decodes before this script runs. Marking it
      // pending would show a shimmer for one frame and then take it away.
      if (img.complete && img.naturalWidth > 0) {
        ready(box, card);
        continue;
      }

      watchPending(img, box, card);
    }
  }

  /**
   * A skeleton nobody can see is not a skeleton, it is an animation.
   *
   * Everything below the fold holds a lazy image, so on a slow connection —
   * or in a lab run that never scrolls — those images never arrive and their
   * shimmer runs for the life of the page. That was seventy boxes repainting
   * every frame to fill a gap out of view. The shimmer now starts when the
   * box is near the viewport, which is the only time it says anything.
   */
  function watchPending(img, box, card) {
    if (!pendingObserver) {
      markPending(img, box, card);
      return;
    }
    waiting.set(box, { img: img, card: card });
    pendingObserver.observe(box);
  }

  var waiting = new WeakMap();

  // Measured against the window as it was, not as a screenshot tool may have
  // stretched it: those expand the viewport to the whole document at once.
  var viewportHeight = window.innerHeight;
  window.addEventListener('orientationchange', function () {
    setTimeout(function () { viewportHeight = window.innerHeight; }, 300);
  });

  var pendingObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              var box = entries[i].target;
              if (!entries[i].isIntersecting) continue;

              var rect = entries[i].boundingClientRect;
              if (rect.top >= viewportHeight + 200 || rect.bottom <= -200) continue;

              pendingObserver.unobserve(box);
              var held = waiting.get(box);
              waiting.delete(box);
              if (!held) continue;

              // It may well have arrived during the scroll down to it.
              if (held.img.complete && held.img.naturalWidth > 0) ready(box, held.card);
              else markPending(held.img, box, held.card);
            }
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  function markPending(img, box, card) {
    box.setAttribute('data-fp-media', 'pending');
    if (card) card.setAttribute('data-fp-card', 'pending');

    var watchdog = 0;

    var done = function () {
      clearTimeout(watchdog);
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      ready(box, card);
    };
    img.addEventListener('load', done);
    // A broken image should not shimmer forever; let the alt text through.
    img.addEventListener('error', done);

    // A request the browser abandons — a slideshow moving on, a dropped
    // connection — fires neither event, and the shimmer would sit there.
    watchdog = setTimeout(done, 10000);

    // The image can finish between the check above and this listener, in
    // which case its load event is already spent.
    if (img.complete) done();
  }

  // The first pass waits for a quiet moment: the first row of photos is eager
  // and needs no shimmer, and on a long collection page this pass (every image
  // on the page) was competing with that first paint on a phone.
  var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1); };
  idle(function () { scan(document); }, { timeout: 1200 });

  // Recommendations morph in, pagination appends, and the brand finder builds
  // its cards on click — all after this first pass.
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType === 1) scan(added[j]);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  /* --- Collection reload ------------------------------------------------ */

  function skeletonCard(tag) {
    var item = document.createElement(tag);
    item.className = 'fp-skel-card';
    item.setAttribute('aria-hidden', 'true');
    item.innerHTML =
      '<span class="fp-skel fp-skel-card__media"></span>' +
      '<span class="fp-skel fp-skel-card__line"></span>' +
      '<span class="fp-skel fp-skel-card__line fp-skel-card__line--short"></span>';
    return item;
  }

  /**
   * Replace the product grid with placeholders, keeping the count the page
   * already had so the layout holds its shape until the new page paints.
   */
  function grid() {
    var list = document.querySelector('.product-grid, .fp-product-grid');
    if (!list || list.hasAttribute('data-fp-skeleton')) return;

    // Horizon parks a <script> and a <style> inside the grid, so neither the
    // count nor the tag can come from `children` as it stands.
    var items = [].filter.call(list.children, function (node) {
      return !/^(SCRIPT|STYLE|TEMPLATE|LINK)$/.test(node.tagName);
    });

    var count = items.length || 6;
    if (count > 12) count = 12;

    // A list wants list items; anything else takes a div. Reading the tag off
    // the first child would inherit whatever Horizon happened to put first.
    var tag = /^(UL|OL)$/.test(list.tagName) ? 'li' : 'div';

    list.setAttribute('data-fp-skeleton', '');
    list.setAttribute('aria-busy', 'true');
    list.innerHTML = '';
    for (var i = 0; i < count; i++) list.appendChild(skeletonCard(tag));

    // The grid is usually below the filter bar that triggered this; without
    // it the placeholders can all sit off screen and the click reads as dead.
    var top = list.getBoundingClientRect().top;
    if (top < 0) list.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  window.fpSkeleton = { grid: grid, scan: scan };

  // The filter bar's own selects call `grid()` directly. Its pills and any
  // pagination links are plain anchors, so catch those here — but only a
  // genuine navigation: a new tab or a modified click leaves this page up.
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target.closest && event.target.closest('a[href]');
    if (!link || link.target === '_blank') return;
    // Leave real cards in place for page navigation: the browser may restore
    // this exact DOM from its back/forward cache when a shopper returns.
    if (!link.closest('.fp-shop-pills')) return;

    var href = link.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return;

    grid();
  });
})();
